/**
 * Справочник назначений: для чего лекарство лежит в аптечке.
 *
 * Дома аптечку перебирают не по названиям, а по вопросу «что у меня есть от
 * горла». Названия препаратов человек не помнит — ни торговые, ни тем более
 * МНН, — зато всегда знает, что ищет. Поэтому назначение и задаёт порядок в
 * аптечке, а не алфавит и не форма выпуска.
 *
 * Ключи стабильны: они лежат в базе в `Medication.purposeKey`. Переименовывать
 * нельзя, можно менять подпись и значок. Своё назначение хранится как
 * `custom:Название` — тем же способом, что и свои отделы в покупках: отдельная
 * таблица ради строки не нужна, а правило разбора живёт в одном месте.
 */

import {
  Bandage,
  Bug,
  Eye,
  Flower2,
  HeartPulse,
  type LucideIcon,
  Moon,
  Package,
  Snowflake,
  Soup,
  Sparkles,
  Sun,
  Thermometer,
  Zap,
} from 'lucide-react';

export const MEDICATION_PURPOSE_KEYS = [
  'fever',
  'pain',
  'cold',
  'allergy',
  'stomach',
  'heart',
  'antibiotic',
  'wound',
  'eyesEars',
  'vitamins',
  'sleep',
] as const;

export type MedicationPurposeKey = (typeof MEDICATION_PURPOSE_KEYS)[number];

export interface MedicationPurpose {
  /** Ключ из справочника либо `custom:Название`. Пустой — назначение не указано. */
  key: string;
  title: string;
  icon: LucideIcon;
}

/**
 * Порядок — по тому, как часто в аптечку лезут: температура и боль впереди,
 * витамины и снотворное в конце.
 */
export const MEDICATION_PURPOSES: readonly (MedicationPurpose & { key: MedicationPurposeKey })[] = [
  { key: 'fever', title: 'Температура', icon: Thermometer },
  { key: 'pain', title: 'Боль', icon: Zap },
  { key: 'cold', title: 'Простуда, горло и нос', icon: Snowflake },
  { key: 'allergy', title: 'Аллергия', icon: Flower2 },
  { key: 'stomach', title: 'Живот и пищеварение', icon: Soup },
  { key: 'heart', title: 'Сердце и давление', icon: HeartPulse },
  { key: 'antibiotic', title: 'Антибиотики', icon: Bug },
  { key: 'wound', title: 'Раны и ожоги', icon: Bandage },
  { key: 'eyesEars', title: 'Глаза и уши', icon: Eye },
  { key: 'vitamins', title: 'Витамины и БАД', icon: Sun },
  { key: 'sleep', title: 'Нервы и сон', icon: Moon },
];

export const CUSTOM_PURPOSE_PREFIX = 'custom:';

/** Назначение без справочника — человек вписал своё. */
export function isCustomPurpose(key: string): boolean {
  return key.startsWith(CUSTOM_PURPOSE_PREFIX);
}

/** Ключ для своего назначения. Пустое название ключа не образует. */
export function customPurposeKey(title: string): string | null {
  const value = title.trim();
  return value ? `${CUSTOM_PURPOSE_PREFIX}${value}` : null;
}

const PURPOSE_INDEX = new Map(MEDICATION_PURPOSES.map((purpose) => [purpose.key, purpose]));

/** Назначение, которое ещё не указали: отдельная группа, а не «прочее». */
export const UNSET_PURPOSE: MedicationPurpose = {
  key: '',
  title: 'Без назначения',
  icon: Package,
};

/**
 * Назначение по ключу.
 *
 * Неизвестный ключ — это назначение, которое удалили из справочника; такое
 * лекарство не должно пропасть из аптечки, поэтому попадает в «Без
 * назначения», а не отбрасывается.
 */
export function getPurpose(key: string | null): MedicationPurpose {
  if (!key) return UNSET_PURPOSE;
  if (isCustomPurpose(key)) {
    return { key, title: key.slice(CUSTOM_PURPOSE_PREFIX.length), icon: Sparkles };
  }
  return PURPOSE_INDEX.get(key as MedicationPurposeKey) ?? UNSET_PURPOSE;
}

/**
 * Свои назначения, встречающиеся среди переданных ключей.
 *
 * Собираются из самих лекарств — отдельного списка «моих назначений» нет
 * намеренно: он копился бы назначениями, которыми больше не пользуются.
 * Заведённое однажды предлагается дальше, а не набирается руками у каждого
 * следующего лекарства.
 */
export function customPurposes(keys: Iterable<string | null>): MedicationPurpose[] {
  const found = new Map<string, MedicationPurpose>();

  for (const key of keys) {
    if (key && isCustomPurpose(key) && !found.has(key)) found.set(key, getPurpose(key));
  }

  return [...found.values()].sort((a, b) => a.title.localeCompare(b.title, 'ru'));
}

/**
 * Ключ назначения по названию, набранному руками.
 *
 * Набранная «Температура» — это назначение из справочника, а не второе такое
 * же рядом. «мигрень» строчными — то самое назначение, которое человек завёл
 * вчера: иначе в аптечке появляются две одинаковые группы по одной строке, и
 * непонятно, почему лекарства не сходятся вместе.
 */
export function purposeKeyFor(title: string, known: Iterable<string | null> = []): string | null {
  const trimmed = title.trim();
  if (!trimmed) return null;

  const lower = trimmed.toLowerCase();

  const fromCatalog = MEDICATION_PURPOSES.find(
    (purpose) => purpose.title.toLowerCase() === lower,
  );
  if (fromCatalog) return fromCatalog.key;

  const same = customPurposes(known).find((purpose) => purpose.title.toLowerCase() === lower);
  if (same) return same.key;

  return customPurposeKey(trimmed);
}

export interface PurposeGroup<T> {
  purpose: MedicationPurpose;
  items: T[];
}

/**
 * Раскладывает аптечку по назначениям.
 *
 * Порядок групп: справочник в своём порядке, затем свои назначения по
 * алфавиту, «Без назначения» — последним. Пустые группы не создаются: пустой
 * заголовок «Аллергия» сообщает только о том, что справочник длиннее аптечки.
 */
export function groupByPurpose<T extends { purposeKey: string | null }>(
  items: readonly T[],
): PurposeGroup<T>[] {
  const groups = new Map<string, T[]>();

  for (const item of items) {
    const key = getPurpose(item.purposeKey).key;
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }

  const known = MEDICATION_PURPOSES.filter((purpose) => groups.has(purpose.key)).map((purpose) => ({
    purpose,
    items: groups.get(purpose.key) ?? [],
  }));

  const custom = [...groups.keys()]
    .filter(isCustomPurpose)
    .sort((a, b) => a.localeCompare(b, 'ru'))
    .map((key) => ({ purpose: getPurpose(key), items: groups.get(key) ?? [] }));

  const unset = groups.has(UNSET_PURPOSE.key)
    ? [{ purpose: UNSET_PURPOSE, items: groups.get(UNSET_PURPOSE.key) ?? [] }]
    : [];

  return [...known, ...custom, ...unset];
}
